import { DropdownOption } from "../components/shared/TailwindComponents/Dropdown";
import i18n from 'i18next';
import { translate } from '../i18n/translate';

/**
 * Maps i18n language codes (short, e.g. 'sv') to BCP-47 locale strings
 * suitable for `Intl.*` APIs. Covers every language the app currently
 * ships translations for (see src/i18n/i18n.tsx). When a new language
 * is added there, add its mapping here too.
 *
 * Exported so callers anywhere in the app can format dates / numbers /
 * lists in the user's locale without re-implementing the mapping.
 */
export const getIntlLocale = (): string => {
  const langMap: Record<string, string> = {
    'en': 'en-GB',
    'sv': 'sv-SE',
    'nb': 'nb-NO',
    'es': 'es-ES',
    'nl': 'nl-NL',
    'de': 'de-DE',
    'fi': 'fi-FI',
    'da': 'da-DK',
    'he': 'he-IL',
    'bg': 'bg-BG',
  };
  return langMap[i18n.language] || i18n.language || 'en-GB';
};

export const getVoiceID = (voiceName: string, voiceList: any[]) => {
    const voice = voiceList.find(v => v.DisplayName === voiceName);
    return voice ? voice.ID : 'en-US-JennyNeural'; 
  };

  export const formatCamelCase = (text: string): string => {
    if (!text) return '';
    return text
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/_/g, ' ')
      .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2')
      .replace(/(^|\s)\S/g, (char) => char.toUpperCase());
  };
  

  /** Ensures a date string without timezone info is treated as UTC */
  export function ensureUTCString(dateStr: string): string {
    const trimmed = dateStr.trim();
    if (trimmed.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(trimmed) || /GMT|UTC$/i.test(trimmed)) return trimmed;
    return trimmed.replace(' ', 'T') + 'Z';
  }

  /**
   * THE way to turn a backend timestamp into a `Date`.
   *
   * The backend stores UTC wall-clock in naive columns and serialises it
   * without a `Z`, so a bare `new Date(str)` parses it as LOCAL time — off by
   * the viewer's UTC offset, which is exactly how a task due later today gets
   * drawn as overdue in the evening. Every comparison (overdue, "due soon",
   * days remaining) and every picker seed goes through here.
   *
   * Returns `null` for empty or unparseable input so callers can bail rather
   * than propagate an Invalid Date.
   */
  export function parseServerDate(dateStr: string | null | undefined): Date | null {
    if (!dateStr) return null;
    const date = new Date(ensureUTCString(dateStr));
    return Number.isNaN(date.getTime()) ? null : date;
  }

  export function formatDate(dateStr: string | undefined): string {
    if (!dateStr) return '-';
    const date = new Date(ensureUTCString(dateStr));
    if (isNaN(date.getTime())) return '-';
    return new Intl.DateTimeFormat(getIntlLocale(), {
      year: 'numeric',
      month: 'long',
      day: '2-digit',
    }).format(date);
  }

  export function formatDateTime(dateStr: string | undefined): string {
    if (!dateStr) return '-';
    const date = new Date(ensureUTCString(dateStr));
    if (isNaN(date.getTime())) return '-';
    return new Intl.DateTimeFormat(getIntlLocale(), {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(date);
  }

export const formatTime = (timestamp: any) => {
  const date = new Date(timestamp);
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  // Omit hour12 — the locale decides 12h vs 24h (en-US/en-GB → 12h or 24h
  // by convention; sv/de/nb/da/fi/nl/he/es default to 24h).
  return date.toLocaleTimeString(getIntlLocale(), { hour: '2-digit', minute: '2-digit', timeZone });
};

export const normalizeToMidnight = (date: Date, timeZone: string): Date => {
  const localizedDate = new Date(date.toLocaleString('en-US', { timeZone }));
  localizedDate.setHours(0, 0, 0, 0);
  return localizedDate;
};

export const formatDateChat = (timestamp: any) => {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const locale = getIntlLocale();
  const date = new Date(timestamp);
  const now = new Date();
  const dateInTimeZone = new Date(date.toLocaleString('en-US', { timeZone }));
  const nowInTimeZone = new Date(now.toLocaleString('en-US', { timeZone }));
  dateInTimeZone.setHours(0, 0, 0, 0);
  nowInTimeZone.setHours(0, 0, 0, 0);
  const differenceInDays = Math.floor((nowInTimeZone.getTime() - dateInTimeZone.getTime()) / (1000 * 60 * 60 * 24));
  if (differenceInDays === 0) return translate('Today');
  if (differenceInDays === 1) return translate('Yesterday');
  if (differenceInDays <= 3) return dateInTimeZone.toLocaleDateString(locale, { weekday: 'long', timeZone });
  return `${dateInTimeZone.toLocaleDateString(locale, { month: 'short', day: '2-digit', timeZone })}, ${dateInTimeZone.getFullYear()}`;
};

  export const statusOptions: DropdownOption[] = [
      { value: 'draft', label: ('Draft') },
      { value: 'active', label: ('Active') },
      { value: 'completed', label: ('Completed') },
      { value: 'under_review', label: ('Under Review') },
      { value: 'pending', label: ('Pending') },
      { value: 'closed', label: ('Closed') },
      { value: 'archived', label: ('Archived') },
      { value: 'on_hold', label: ('On Hold') },
    ];
  
    export const getStatusColor = (status: string | undefined | null): string => {
      if (!status || typeof status !== 'string' || status.trim() === '') {
        return 'gray';
      }

      switch (status.toLowerCase()) {
        case 'draft': return 'gray';
        case 'active': return 'green';
        case 'completed': return 'blue';
        case 'under_review': return 'yellow';
        case 'pending': return 'yellow';
        case 'closed': return 'red';
        case 'archived': return 'purple';
        case 'on_hold': return 'blue';
        case 'open': return 'yellow';
        case 'in progress': return 'blue';
        default: return 'gray';
      }
    };

    export function formatMoney(input: string): string {
      const strInput = String(input);
      
      const lastDot = strInput.lastIndexOf('.');
      const lastComma = strInput.lastIndexOf(',');
    
      let integerPart: string;
      let decimalPart: string = '';
    
      if (lastDot > lastComma) {
        integerPart = strInput.substring(0, lastDot);
        decimalPart = strInput.substring(lastDot + 1);
      } else if (lastComma > lastDot) {
        integerPart = strInput.substring(0, lastComma);
        decimalPart = strInput.substring(lastComma + 1);
      } else {
        integerPart = strInput;
      }
    
      integerPart = integerPart.replace(/\D/g, '').replace(/^0+/, '') || '0';
    
      const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    
      decimalPart = decimalPart.replace(/\D/g, '');
    
      if (decimalPart) {
        return formattedInteger + ',' + decimalPart;
      } else {
        return formattedInteger;
      }
    }
    
    export const camelCaseLabel = (label: string) => label.replace(/_/g, ' ').replace(/(^|\s)\S/g, c => c.toUpperCase());

    export const replaceUnderscoresWithSpaces = (input: string): string => {
      return input.replace(/_/g, ' ');
    };

    export const formatRole = (role: string): string => {
      if (!role) return '';
      return role
        .replace(/_/g, ' ')
        .replace(/(^|\s)\S/g, (char) => char.toUpperCase());
    };

    export const currencies = [
      { value: 'EUR', label: 'Euro', symbol: '€' },
      { value: 'USD', label: 'United States Dollar', symbol: '$' },
      { value: 'GBP', label: 'British Pound', symbol: '£' },
      { value: 'CHF', label: 'Swiss Franc', symbol: 'CHF' },
      { value: 'NOK', label: 'Norwegian Krone', symbol: 'NOK' },
      { value: 'SEK', label: 'Swedish Krona', symbol: 'SEK' },
      { value: 'DKK', label: 'Danish Krone', symbol: 'DKK' },
      { value: 'ISK', label: 'Icelandic Króna', symbol: 'ISK' },
      { value: 'CAD', label: 'Canadian Dollar', symbol: '$' },
      { value: 'AUD', label: 'Australian Dollar', symbol: '$' },
      { value: 'JPY', label: 'Japanese Yen', symbol: '¥' },
      { value: 'CNY', label: 'Chinese Yuan', symbol: '¥' },
      { value: 'INR', label: 'Indian Rupee', symbol: '₹' },
      { value: 'AED', label: 'United Arab Emirates Dirham', symbol: 'د.إ' },
      { value: 'AFN', label: 'Afghan Afghani', symbol: '؋' },
      { value: 'ALL', label: 'Albanian Lek', symbol: 'L' },
      { value: 'AMD', label: 'Armenian Dram', symbol: '֏' },
      { value: 'ANG', label: 'Netherlands Antillean Guilder', symbol: 'ƒ' },
      { value: 'AOA', label: 'Angolan Kwanza', symbol: 'Kz' },
      { value: 'ARS', label: 'Argentine Peso', symbol: '$' },
      { value: 'AWG', label: 'Aruban Florin', symbol: 'ƒ' },
      { value: 'AZN', label: 'Azerbaijani Manat', symbol: '₼' },
      { value: 'BAM', label: 'Bosnia-Herzegovina Convertible Mark', symbol: 'KM' },
      { value: 'BBD', label: 'Barbadian Dollar', symbol: '$' },
      { value: 'BDT', label: 'Bangladeshi Taka', symbol: '৳' },
      { value: 'BGN', label: 'Bulgarian Lev', symbol: 'лв' },
      { value: 'BHD', label: 'Bahraini Dinar', symbol: '.د.ب' },
      { value: 'BIF', label: 'Burundian Franc', symbol: 'FBu' },
      { value: 'BMD', label: 'Bermudian Dollar', symbol: '$' },
      { value: 'BND', label: 'Brunei Dollar', symbol: '$' },
      { value: 'BOB', label: 'Bolivian Boliviano', symbol: 'Bs.' },
      { value: 'BRL', label: 'Brazilian Real', symbol: 'R$' },
      { value: 'BSD', label: 'Bahamian Dollar', symbol: '$' },
      { value: 'BTC', label: 'Bitcoin', symbol: '₿' },
      { value: 'BTN', label: 'Bhutanese Ngultrum', symbol: 'Nu.' },
      { value: 'BWP', label: 'Botswana Pula', symbol: 'P' },
      { value: 'BYN', label: 'Belarusian Ruble', symbol: 'Br' },
      { value: 'BZD', label: 'Belize Dollar', symbol: '$' },
      { value: 'CDF', label: 'Congolese Franc', symbol: 'FC' },
      { value: 'CLP', label: 'Chilean Peso', symbol: '$' },
      { value: 'COP', label: 'Colombian Peso', symbol: '$' },
      { value: 'CRC', label: 'Costa Rican Colón', symbol: '₡' },
      { value: 'CUC', label: 'Cuban Convertible Peso', symbol: '$' },
      { value: 'CUP', label: 'Cuban Peso', symbol: '$' },
      { value: 'CVE', label: 'Cape Verdean Escudo', symbol: '$' },
      { value: 'CZK', label: 'Czech Koruna', symbol: 'Kč' },
      { value: 'DJF', label: 'Djiboutian Franc', symbol: 'Fdj' },
      { value: 'DOP', label: 'Dominican Peso', symbol: '$' },
      { value: 'DZD', label: 'Algerian Dinar', symbol: 'د.ج' },
      { value: 'EGP', label: 'Egyptian Pound', symbol: '£' },
      { value: 'ERN', label: 'Eritrean Nakfa', symbol: 'Nfk' },
      { value: 'ETB', label: 'Ethiopian Birr', symbol: 'Br' },
      { value: 'FJD', label: 'Fijian Dollar', symbol: '$' },
      { value: 'FKP', label: 'Falkland Islands Pound', symbol: '£' },
      { value: 'GEL', label: 'Georgian Lari', symbol: '₾' },
      { value: 'GGP', label: 'Guernsey Pound', symbol: '£' },
      { value: 'GHS', label: 'Ghanaian Cedi', symbol: '₵' },
      { value: 'GIP', label: 'Gibraltar Pound', symbol: '£' },
      { value: 'GMD', label: 'Gambian Dalasi', symbol: 'D' },
      { value: 'GNF', label: 'Guinean Franc', symbol: 'FG' },
      { value: 'GTQ', label: 'Guatemalan Quetzal', symbol: 'Q' },
      { value: 'GYD', label: 'Guyanese Dollar', symbol: '$' },
      { value: 'HKD', label: 'Hong Kong Dollar', symbol: '$' },
      { value: 'HNL', label: 'Honduran Lempira', symbol: 'L' },
      { value: 'HRK', label: 'Croatian Kuna', symbol: 'kn' },
      { value: 'HTG', label: 'Haitian Gourde', symbol: 'G' },
      { value: 'HUF', label: 'Hungarian Forint', symbol: 'Ft' },
      { value: 'IDR', label: 'Indonesian Rupiah', symbol: 'Rp' },
      { value: 'ILS', label: 'Israeli New Shekel', symbol: '₪' },
      { value: 'IMP', label: 'Manx Pound', symbol: '£' },
      { value: 'IQD', label: 'Iraqi Dinar', symbol: 'ع.د' },
      { value: 'IRR', label: 'Iranian Rial', symbol: '﷼' },
      { value: 'JEP', label: 'Jersey Pound', symbol: '£' },
      { value: 'JMD', label: 'Jamaican Dollar', symbol: '$' },
      { value: 'JOD', label: 'Jordanian Dinar', symbol: 'د.ا' },
      { value: 'KES', label: 'Kenyan Shilling', symbol: 'Sh' },
      { value: 'KGS', label: 'Kyrgyzstani Som', symbol: 'с' },
      { value: 'KHR', label: 'Cambodian Riel', symbol: '៛' },
      { value: 'KMF', label: 'Comorian Franc', symbol: 'CF' },
      { value: 'KPW', label: 'North Korean Won', symbol: '₩' },
      { value: 'KRW', label: 'South Korean Won', symbol: '��' },
      { value: 'KWD', label: 'Kuwaiti Dinar', symbol: 'د.ك' },
      { value: 'KYD', label: 'Cayman Islands Dollar', symbol: '$' },
      { value: 'KZT', label: 'Kazakhstani Tenge', symbol: '₸' },
      { value: 'LAK', label: 'Lao Kip', symbol: '₭' },
      { value: 'LBP', label: 'Lebanese Pound', symbol: 'ل.ل' },
      { value: 'LKR', label: 'Sri Lankan Rupee', symbol: 'Rs' },
      { value: 'LRD', label: 'Liberian Dollar', symbol: '$' },
      { value: 'LSL', label: 'Lesotho Loti', symbol: 'L' },
      { value: 'LYD', label: 'Libyan Dinar', symbol: 'ل.د' },
      { value: 'MAD', label: 'Moroccan Dirham', symbol: 'د.م.' },
      { value: 'MDL', label: 'Moldovan Leu', symbol: 'L' },
      { value: 'MGA', label: 'Malagasy Ariary', symbol: 'Ar' },
      { value: 'MKD', label: 'Macedonian Denar', symbol: 'ден' },
      { value: 'MMK', label: 'Myanmar Kyat', symbol: 'Ks' },
      { value: 'MNT', label: 'Mongolian Tögrög', symbol: '₮' },
      { value: 'MOP', label: 'Macanese Pataca', symbol: 'P' },
      { value: 'MRU', label: 'Mauritanian Ouguiya', symbol: 'UM' },
      { value: 'MUR', label: 'Mauritian Rupee', symbol: '₨' },
      { value: 'MVR', label: 'Maldivian Rufiyaa', symbol: '.ރ' },
      { value: 'MWK', label: 'Malawian Kwacha', symbol: 'MK' },
      { value: 'MXN', label: 'Mexican Peso', symbol: '$' },
      { value: 'MYR', label: 'Malaysian Ringgit', symbol: 'RM' },
      { value: 'MZN', label: 'Mozambican Metical', symbol: 'MT' },
      { value: 'NAD', label: 'Namibian Dollar', symbol: '$' },
      { value: 'NGN', label: 'Nigerian Naira', symbol: '₦' },
      { value: 'NIO', label: 'Nicaraguan Córdoba', symbol: 'C$' },
      { value: 'NPR', label: 'Nepalese Rupee', symbol: '₨' },
      { value: 'NZD', label: 'New Zealand Dollar', symbol: '$' },
      { value: 'OMR', label: 'Omani Rial', symbol: 'ر.ع.' },
      { value: 'PAB', label: 'Panamanian Balboa', symbol: 'B/.' },
      { value: 'PEN', label: 'Peruvian Sol', symbol: 'S/.' },
      { value: 'PGK', label: 'Papua New Guinean Kina', symbol: 'K' },
      { value: 'PHP', label: 'Philippine Peso', symbol: '₱' },
      { value: 'PKR', label: 'Pakistani Rupee', symbol: '₨' },
      { value: 'PLN', label: 'Polish Złoty', symbol: 'zł' },
      { value: 'PYG', label: 'Paraguayan Guaraní', symbol: '₲' },
      { value: 'QAR', label: 'Qatari Riyal', symbol: 'ر.ق' },
      { value: 'RON', label: 'Romanian Leu', symbol: 'lei' },
      { value: 'RSD', label: 'Serbian Dinar', symbol: 'дин.' },
      { value: 'RUB', label: 'Russian Ruble', symbol: '₽' },
      { value: 'RWF', label: 'Rwandan Franc', symbol: 'FRw' },
      { value: 'SAR', label: 'Saudi Riyal', symbol: 'ر.س' },
      { value: 'SBD', label: 'Solomon Islands Dollar', symbol: '$' },
      { value: 'SCR', label: 'Seychellois Rupee', symbol: '₨' },
      { value: 'SDG', label: 'Sudanese Pound', symbol: 'ج.س.' },
      { value: 'SGD', label: 'Singapore Dollar', symbol: '$' },
      { value: 'SHP', label: 'Saint Helena Pound', symbol: '£' },
      { value: 'SLL', label: 'Sierra Leonean Leone', symbol: 'Le' },
      { value: 'SOS', label: 'Somali Shilling', symbol: 'Sh' },
      { value: 'SRD', label: 'Surinamese Dollar', symbol: '$' },
      { value: 'SSP', label: 'South Sudanese Pound', symbol: '£' },
      { value: 'STN', label: 'São Tomé and Príncipe Dobra', symbol: 'Db' },
      { value: 'SVC', label: 'Salvadoran Colón', symbol: '₡' },
      { value: 'SYP', label: 'Syrian Pound', symbol: '£' },
      { value: 'SZL', label: 'Swazi Lilangeni', symbol: 'L' },
      { value: 'THB', label: 'Thai Baht', symbol: '฿' },
      { value: 'TJS', label: 'Tajikistani Somoni', symbol: 'SM' },
      { value: 'TMT', label: 'Turkmenistani Manat', symbol: 'm' },
      { value: 'TND', label: 'Tunisian Dinar', symbol: 'د.ت' },
      { value: 'TOP', label: 'Tongan Paʻanga', symbol: 'T$' },
      { value: 'TRY', label: 'Turkish Lira', symbol: '₺' },
      { value: 'TTD', label: 'Trinidad and Tobago Dollar', symbol: '$' },
      { value: 'TWD', label: 'New Taiwan Dollar', symbol: '$' },
      { value: 'TZS', label: 'Tanzanian Shilling', symbol: 'Sh' },
      { value: 'UAH', label: 'Ukrainian Hryvnia', symbol: '₴' },
      { value: 'UGX', label: 'Ugandan Shilling', symbol: 'Sh' },
      { value: 'UYU', label: 'Uruguayan Peso', symbol: '$' },
      { value: 'UZS', label: 'Uzbekistani Som', symbol: "so'm" },
      { value: 'VES', label: 'Venezuelan Bolívar Soberano', symbol: 'Bs.S.' },
      { value: 'VND', label: 'Vietnamese Đồng', symbol: '₫' },
      { value: 'VUV', label: 'Vanuatu Vatu', symbol: 'Vt' },
      { value: 'WST', label: 'Samoan Tala', symbol: 'T' },
      { value: 'XAF', label: 'Central African CFA Franc', symbol: 'FCFA' },
      { value: 'XCD', label: 'East Caribbean Dollar', symbol: '$' },
      { value: 'XOF', label: 'West African CFA Franc', symbol: 'CFA' },
      { value: 'XPF', label: 'CFP Franc', symbol: '₣' },
      { value: 'YER', label: 'Yemeni Rial', symbol: '﷼' },
      { value: 'ZAR', label: 'South African Rand', symbol: 'R' },
      { value: 'ZMW', label: 'Zambian Kwacha', symbol: 'ZK' },
      { value: 'ZWL', label: 'Zimbabwean Dollar', symbol: '$' },
    ];

  export const formatEventType = (eventType: string): string => {
    if (!eventType) return '';
    return eventType
      .replace(/_/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  export const formatInsightName = (name: string): string => {
    if (!name) return '';
    return name
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  export const formatStatus = (status: string): string => {
    if (!status) return '';
    const formatted = status.replace(/_/g, ' ').toLowerCase();
    return formatted.split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  export const formatFieldName = (fieldName: string): string => {
    if (!fieldName) return '';
    return fieldName
      .replace(/_/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  export const formatBytes = (bytes: number | undefined): string => {
    if (bytes === undefined || bytes === null || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  export const formatDocumentName = (fileName: string): { name: string; extension: string } => {
    if (!fileName) return { name: '', extension: '' };

    // Extract extension
    const lastDotIndex = fileName.lastIndexOf('.');
    let nameWithoutExt = fileName;
    let extension = '';

    if (lastDotIndex > 0) {
      extension = fileName.substring(lastDotIndex + 1).toLowerCase();
      nameWithoutExt = fileName.substring(0, lastDotIndex);
    }

    // Remove underscores and format the name
    const formattedName = nameWithoutExt
      .replace(/_/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');

    return {
      name: formattedName,
      extension: extension,
    };
  };
